// Render-crash beacon
//
// iOS WebKit kills a tab's renderer process when it exceeds the per-tab
// memory/GPU budget (shown to the user as "Un problème récurrent est survenu").
// That kill destroys the JS context instantly, so the page can never report the
// crash itself — which is why these crashes show up in Sentry as "0 users
// impacted". This module makes them attributable:
//
//   1. armRenderGuard()  — called right before a heavy view paints. Writes a
//      record to localStorage.
//   2. disarmRenderGuard() — called once the view has painted and survived.
//      Removes the record. Also called automatically on pagehide / the page
//      going hidden, since leaving the page is not a crash.
//   3. reportPreviousRenderCrash() — called on app boot. If a record is still
//      present, the previous load died mid-render before it could disarm, so we
//      report it (and clear it).
//
// All localStorage access is wrapped in try/catch because it can throw in
// private mode / when the quota is exceeded.

const KEY = 'kaiylo:render-crash-guard';
const COUNT_KEY = 'kaiylo:render-crash-count';
const SAFE_MODE_KEY = 'kaiylo:safe-mode-until';

// Only report a surviving guard if it is recent. A real renderer crash loop
// reloads within seconds; anything older is almost certainly a stale record
// from a tab the user simply closed, and reporting it would just add noise.
const MAX_AGE_MS = 5 * 60 * 1000;

// Après ce nombre de crashs consécutifs, l'app démarre en « mode sans échec »
// (sans PostHog) pendant SAFE_MODE_DURATION_MS : un appareil pris dans la
// boucle « Un problème récurrent est survenu » s'en sort seul, sans devoir
// vider les données Safari à la main.
const SAFE_MODE_AFTER_CRASHES = 2;
const SAFE_MODE_DURATION_MS = 24 * 60 * 60 * 1000;

const readCount = () => {
  try {
    const n = parseInt(localStorage.getItem(COUNT_KEY), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
};

const writeCount = (n) => {
  try {
    if (n > 0) localStorage.setItem(COUNT_KEY, String(n));
    else localStorage.removeItem(COUNT_KEY);
  } catch {
    // best-effort
  }
};

// A-t-on un crash pendant (garde armée récente laissée par le chargement
// précédent) ? Lecture seule — reportPreviousRenderCrash fait le nettoyage.
const hasPendingRecentCrash = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    const age = typeof data.ts === 'number' ? Date.now() - data.ts : 0;
    return age <= MAX_AGE_MS;
  } catch {
    return false;
  }
};

/**
 * À appeler AVANT Sentry.init / initAnalytics. Décide si ce chargement doit
 * démarrer allégé. Vrai si : le seuil de crashs consécutifs vient d'être
 * atteint (active alors le mode pour 24 h), ou si un mode déjà actif court
 * toujours.
 */
export function shouldBootSafeMode() {
  try {
    const count = readCount() + (hasPendingRecentCrash() ? 1 : 0);
    if (count >= SAFE_MODE_AFTER_CRASHES) {
      localStorage.setItem(SAFE_MODE_KEY, String(Date.now() + SAFE_MODE_DURATION_MS));
      return true;
    }
    const until = parseInt(localStorage.getItem(SAFE_MODE_KEY), 10);
    if (Number.isFinite(until)) {
      if (Date.now() < until) return true;
      localStorage.removeItem(SAFE_MODE_KEY);
    }
    return false;
  } catch {
    return false;
  }
}

// Quitter la page (app en arrière-plan, écran verrouillé, rechargement, mise à
// jour du service worker) n'est PAS un crash. Un renderer WebKit tué pour
// mémoire ne déclenche ni pagehide ni visibilitychange : désarmer sur ces
// événements supprime les faux positifs sans masquer les vrais crashs.
// (Sept. 2026 : 108 « Render crash » remontés depuis Android en 14 jours.)
const onPageHide = () => disarmRenderGuard();
const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') disarmRenderGuard();
};
let exitListenersInstalled = false;

const installExitListeners = () => {
  if (exitListenersInstalled || typeof window === 'undefined') return;
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
  exitListenersInstalled = true;
};

const removeExitListeners = () => {
  if (!exitListenersInstalled) return;
  window.removeEventListener('pagehide', onPageHide);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  exitListenersInstalled = false;
};

export function armRenderGuard(route, extra = {}) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        route,
        ts: Date.now(),
        ua: navigator.userAgent,
        ...extra,
      })
    );
  } catch {
    // localStorage unavailable (private mode / quota) — beacon is best-effort.
  }
  installExitListeners();
}

export function disarmRenderGuard() {
  removeExitListeners();
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

// Returns true if a previous render crash was found and reported.
// `report` is called with the stored record (e.g. to forward to Sentry).
export function reportPreviousRenderCrash(report) {
  let raw;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return false;
  }
  if (!raw) {
    // Chargement sain : la série de crashs consécutifs est rompue.
    writeCount(0);
    return false;
  }

  // Whatever we found, clear it so we never report the same crash twice.
  disarmRenderGuard();

  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    data = { route: 'unknown' };
  }

  const age = typeof data.ts === 'number' ? Date.now() - data.ts : 0;
  if (age > MAX_AGE_MS) {
    writeCount(0);
    return false;
  }

  const consecutive = readCount() + 1;
  writeCount(consecutive);

  try {
    report({ ...data, ageMs: age, consecutiveCrashes: consecutive });
  } catch {
    // a failing reporter must not break app boot
  }
  return true;
}
